import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { TraversalCompleteToast } from "../../TraversalCompleteToast";
import { useSeenFloorPlanElements } from "../useSeenFloorPlanElements";
import type { UseTraversalModeV3Args, UseTraversalModeV3Result } from "./types";

export const useTraversalModeV3 = ({
  canEdit,
  currentElementTypeId,
  selectedGroupElements,
  isFloorPlanGroupApproved,
  toggleFloorPlanGroupApproval,
}: UseTraversalModeV3Args): UseTraversalModeV3Result => {
  const { t } = useTranslation();

  const { seenIds, markSeen } = useSeenFloorPlanElements({
    elementTypeId: currentElementTypeId,
  });

  const seenCount = useMemo(
    () =>
      selectedGroupElements.filter((element) => seenIds.has(element.id)).length,
    [selectedGroupElements, seenIds],
  );

  const showTraversalCompleteToast = useCallback(() => {
    toast(TraversalCompleteToast, {
      autoClose: false,
      position: "bottom-right",
      data: {
        title: t("smartSurveyor.traversal.allSeenTitle"),
        description: t("smartSurveyor.traversal.allSeenDescription"),
        cancelLabel: t("smartSurveyor.common.cancel"),
        approveLabel: t("smartSurveyor.common.yes"),
        onApprove: () => toggleFloorPlanGroupApproval(currentElementTypeId),
      },
    });
  }, [currentElementTypeId, toggleFloorPlanGroupApproval, t]);

  const markSeenAndCheckCompletion = useCallback(
    (elementId: string, remainingCount?: number) => {
      if (!currentElementTypeId) return;
      const isNewlySeen = !seenIds.has(elementId);
      markSeen(elementId);
      const totalCount = remainingCount ?? selectedGroupElements.length;
      if (
        canEdit &&
        isNewlySeen &&
        !isFloorPlanGroupApproved(currentElementTypeId) &&
        seenCount + 1 >= totalCount
      ) {
        showTraversalCompleteToast();
      }
    },
    [
      canEdit,
      currentElementTypeId,
      seenIds,
      seenCount,
      selectedGroupElements,
      markSeen,
      isFloorPlanGroupApproved,
      showTraversalCompleteToast,
    ],
  );

  return {
    seenIds,
    seenCount,
    markSeen,
    markSeenAndCheckCompletion,
  };
};
